import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Stack,
  Slider,
  Chip,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  ArrowUpward,
  ArrowDownward,
  ArrowBack,
  ArrowForward,
  ZoomIn,
  ZoomOut,
  RotateLeft,
  RotateRight,
  Refresh,
  KeyboardArrowUp,
  KeyboardArrowDown,
  FiberManualRecord,
  Stop,
  PlayArrow,
  Delete
} from '@mui/icons-material';
import useWebSocket from '../../hooks/useWebSocket';
import { useAPI } from '../../hooks/useAPI';

const RemoteControl: React.FC = () => {
  const { socket, connected } = useWebSocket(process.env.REACT_APP_WS_URL || 'http://localhost:8000');
  const api = useAPI();
  const [speed, setSpeed] = useState<number>(1);
  const [isControlling, setIsControlling] = useState(false);
  const activeControls = useRef<Map<string, any>>(new Map());
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [activeButtons, setActiveButtons] = useState<Set<string>>(new Set());

  // Camera path recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingPathId, setRecordingPathId] = useState<string | null>(null);
  const [recordingBuffer, setRecordingBuffer] = useState<any[]>([]);
  const [savedPath, setSavedPath] = useState<any | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [nameDialogOpen, setNameDialogOpen] = useState(false);
  const [pathName, setPathName] = useState('Camera Path');
  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const countdownTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const recordingStartTimeRef = useRef<number>(0);

  // Send control commands at regular intervals while button is pressed
  useEffect(() => {
    if (isControlling && socket && connected) {
      intervalRef.current = setInterval(() => {
        activeControls.current.forEach((data, eventName) => {
          console.log('RemoteControl: Emitting', eventName, { ...data, speed });
          socket.emit(eventName, { ...data, speed });
        });
      }, 100); // Send updates every 100ms

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [isControlling, speed, socket, connected]);

  const handleControlStart = (eventName: string, data: any = {}) => {
    activeControls.current.set(eventName, data);
    setIsControlling(true);
  };

  const handleControlStop = (eventName: string) => {
    activeControls.current.delete(eventName);
    if (activeControls.current.size === 0) {
      setIsControlling(false);
    }
  };

  const handleToggleControl = (controlId: string, eventName: string, data: any = {}) => {
    setActiveButtons(prev => {
      const newSet = new Set(prev);
      if (newSet.has(controlId)) {
        // Stop the control
        newSet.delete(controlId);
        activeControls.current.delete(eventName);
        if (activeControls.current.size === 0) {
          setIsControlling(false);
        }
      } else {
        // Start the control
        newSet.add(controlId);
        activeControls.current.set(eventName, data);
        setIsControlling(true);
      }
      return newSet;
    });
  };

  const handleReset = () => {
    if (socket && connected) {
      socket.emit('remote:camera-reset');
    }
  };

  // Fetch saved camera path on mount
  useEffect(() => {
    const fetchPath = async () => {
      try {
        const response = await api.get('/map-cg/camera-path/list');
        if (response.data.success && response.data.data.length > 0) {
          setSavedPath(response.data.data[0]);
        } else {
          setSavedPath(null);
        }
      } catch (error) {
        console.error('Error fetching camera path:', error);
      }
    };

    fetchPath();
    const interval = setInterval(fetchPath, 5000); // Refresh every 5 seconds
    return () => clearInterval(interval);
  }, [api]);

  // Camera path recording functions
  const startRecording = async () => {
    // Start countdown: 3, 2, 1
    setCountdown(3);

    const runCountdown = (count: number) => {
      if (count > 0) {
        countdownTimeoutRef.current = setTimeout(() => {
          setCountdown(count - 1);
          runCountdown(count - 1);
        }, 1000);
      } else {
        // Countdown finished, start actual recording
        startActualRecording();
      }
    };

    runCountdown(3);
  };

  const startActualRecording = async () => {
    try {
      const response = await api.post('/map-cg/camera-path/start', {
        name: 'Camera Path'
      });

      if (response.data.success) {
        setCountdown(null);
        setIsRecording(true);
        setRecordingPathId(response.data.data.pathId);
        setRecordingBuffer([]);
        recordingStartTimeRef.current = response.data.data.startTime;

        // Emit recording started event to LiveCG map
        if (socket && connected) {
          socket.emit('remote:recording-started', { pathId: response.data.data.pathId });

          // Listen for camera position updates from LiveCG
          socket.on('camera:position-update', handleCameraPositionUpdate);
        }
      }
    } catch (error) {
      console.error('Error starting recording:', error);
      setCountdown(null);
    }
  };

  const handleCameraPositionUpdate = (data: any) => {
    // Accumulate camera positions in buffer
    setRecordingBuffer(prev => [...prev, {
      timestamp: Date.now() - recordingStartTimeRef.current,
      center: data.center,
      zoom: data.zoom,
      bearing: data.bearing,
      pitch: data.pitch
    }]);
  };

  const stopRecording = async () => {
    // Clear countdown if active
    if (countdownTimeoutRef.current) {
      clearTimeout(countdownTimeoutRef.current);
      countdownTimeoutRef.current = null;
      setCountdown(null);
    }

    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
      recordingIntervalRef.current = null;
    }

    // Stop listening for camera updates
    if (socket && connected) {
      socket.off('camera:position-update', handleCameraPositionUpdate);
    }

    if (recordingPathId && recordingBuffer.length > 0) {
      try {
        // Send all recorded points
        await api.post('/map-cg/camera-path/record', {
          pathId: recordingPathId,
          points: recordingBuffer
        });

        // Open name dialog
        setNameDialogOpen(true);
      } catch (error) {
        console.error('Error stopping recording:', error);
      }
    }

    setIsRecording(false);
    setRecordingPathId(null);
  };

  const handleSavePath = async () => {
    if (!recordingPathId) return;

    try {
      const response = await api.post('/map-cg/camera-path/stop', {
        pathId: recordingPathId,
        name: pathName
      });

      if (response.data.success) {
        setSavedPath(response.data.data);
        setRecordingBuffer([]);
        setNameDialogOpen(false);
        setPathName('Camera Path');

        // Emit recording stopped event to LiveCG map
        if (socket && connected) {
          socket.emit('remote:recording-stopped', { path: response.data.data });
        }
      }
    } catch (error) {
      console.error('Error saving path:', error);
    }
  };

  const playPath = () => {
    if (socket && connected && savedPath) {
      socket.emit('remote:play-path', { pathId: savedPath.pathId });
    }
  };

  const deletePath = async () => {
    if (!savedPath || !window.confirm('Are you sure you want to delete this path?')) return;

    try {
      await api.delete(`/map-cg/camera-path/${savedPath.pathId}`);
      setSavedPath(null);

      // Emit path deleted event to LiveCG map
      if (socket && connected) {
        socket.emit('remote:path-deleted');
      }
    } catch (error) {
      console.error('Error deleting path:', error);
    }
  };

  // Control button component with toggle mode
  const ControlButton = ({
    icon,
    eventName,
    direction,
    label,
    size = 'large',
    toggleMode = false,
    controlId
  }: {
    icon: React.ReactNode;
    eventName: string;
    direction: string;
    label: string;
    size?: 'small' | 'medium' | 'large';
    toggleMode?: boolean;
    controlId?: string;
  }) => {
    const isActive = controlId ? activeButtons.has(controlId) : false;

    if (toggleMode && controlId) {
      return (
        <IconButton
          onClick={() => handleToggleControl(controlId, eventName, { direction })}
          sx={{
            backgroundColor: isActive ? 'primary.main' : 'rgba(255, 255, 255, 0.1)',
            color: 'white',
            width: size === 'large' ? 80 : size === 'medium' ? 60 : 50,
            height: size === 'large' ? 80 : size === 'medium' ? 60 : 50,
            '&:hover': {
              backgroundColor: isActive ? 'primary.dark' : 'rgba(255, 255, 255, 0.15)'
            },
            border: isActive ? '3px solid #fff' : 'none',
            boxShadow: isActive ? '0 0 20px rgba(33, 150, 243, 0.8)' : 'none',
            animation: isActive ? 'pulse 1.5s infinite' : 'none',
            '@keyframes pulse': {
              '0%, 100%': { transform: 'scale(1)' },
              '50%': { transform: 'scale(1.05)' }
            }
          }}
          aria-label={label}
        >
          {icon}
        </IconButton>
      );
    }

    return (
      <IconButton
        onMouseDown={() => handleControlStart(eventName, { direction })}
        onMouseUp={() => handleControlStop(eventName)}
        onMouseLeave={() => handleControlStop(eventName)}
        onTouchStart={() => handleControlStart(eventName, { direction })}
        onTouchEnd={() => handleControlStop(eventName)}
        sx={{
          backgroundColor: activeControls.current.has(eventName) ? 'primary.main' : 'rgba(255, 255, 255, 0.1)',
          color: 'white',
          width: size === 'large' ? 80 : size === 'medium' ? 60 : 50,
          height: size === 'large' ? 80 : size === 'medium' ? 60 : 50,
          '&:hover': {
            backgroundColor: 'primary.dark'
          },
          '&:active': {
            backgroundColor: 'primary.main'
          }
        }}
        aria-label={label}
      >
        {icon}
      </IconButton>
    );
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#0a0a0a',
        color: 'white',
        padding: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 2
      }}
    >
      {/* Header */}
      <Paper
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          padding: 2,
          borderRadius: 2
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" fontWeight="bold">
            LiveCG Remote Control
          </Typography>
          <Chip
            label={connected ? 'Connected' : 'Disconnected'}
            color={connected ? 'success' : 'error'}
            size="small"
          />
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Control LiveCG map camera in real-time
        </Typography>
      </Paper>

      {/* Camera Path Recording Controls */}
      <Paper
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          padding: 2,
          borderRadius: 2
        }}
      >
        <Typography variant="subtitle2" gutterBottom>
          Camera Path Recording
        </Typography>

        {/* Countdown Overlay */}
        {countdown !== null && countdown > 0 && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.9)',
              zIndex: 9999
            }}
          >
            <Typography
              variant="h1"
              sx={{
                fontSize: '10rem',
                fontWeight: 'bold',
                color: countdown === 1 ? '#22c55e' : '#ef4444',
                animation: 'pulse 0.5s ease-in-out',
                '@keyframes pulse': {
                  '0%': { transform: 'scale(0.8)', opacity: 0 },
                  '50%': { transform: 'scale(1.2)', opacity: 1 },
                  '100%': { transform: 'scale(1)', opacity: 1 }
                }
              }}
            >
              {countdown}
            </Typography>
          </Box>
        )}

        {/* Recording Controls */}
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          {!isRecording ? (
            <Button
              variant="contained"
              startIcon={<FiberManualRecord />}
              onClick={startRecording}
              fullWidth
              sx={{
                backgroundColor: '#ef4444',
                '&:hover': { backgroundColor: '#dc2626' },
                py: 1.5
              }}
            >
              {savedPath ? 'Record (Overwrite)' : 'Start Recording'}
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={<Stop />}
              onClick={stopRecording}
              fullWidth
              sx={{
                backgroundColor: '#ef4444',
                '&:hover': { backgroundColor: '#dc2626' },
                py: 1.5,
                animation: 'blink 1s infinite',
                '@keyframes blink': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.7 }
                }
              }}
            >
              Stop Recording ({recordingBuffer.length} pts)
            </Button>
          )}
        </Stack>

        {/* Saved Path Display */}
        {savedPath && !isRecording && (
          <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', mb: 2 }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {savedPath.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {savedPath.pointCount} points • {(savedPath.duration / 1000).toFixed(1)}s
                    </Typography>
                  </Box>
                  <Chip label="Saved" size="small" color="success" />
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    startIcon={<PlayArrow />}
                    onClick={playPath}
                    size="small"
                    fullWidth
                    sx={{ color: '#22c55e', borderColor: '#22c55e' }}
                  >
                    Play on LiveCG
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Delete />}
                    onClick={deletePath}
                    size="small"
                    fullWidth
                    sx={{ color: '#ef4444', borderColor: '#ef4444' }}
                  >
                    Delete
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        )}

        {!savedPath && !isRecording && (
          <Typography variant="caption" color="text.secondary" textAlign="center" display="block" sx={{ py: 1 }}>
            No camera path recorded. Start recording to capture camera movements.
          </Typography>
        )}
      </Paper>

      {/* Speed Control */}
      <Paper
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          padding: 2,
          borderRadius: 2
        }}
      >
        <Typography variant="subtitle2" gutterBottom>
          Control Speed
        </Typography>
        <Slider
          value={speed}
          onChange={(_, value) => setSpeed(value as number)}
          min={0.5}
          max={3}
          step={0.5}
          marks={[
            { value: 0.5, label: 'Slow' },
            { value: 1.5, label: 'Medium' },
            { value: 3, label: 'Fast' }
          ]}
          sx={{
            color: 'primary.main',
            '& .MuiSlider-markLabel': {
              color: 'rgba(255, 255, 255, 0.7)'
            }
          }}
        />
      </Paper>

      {/* Movement Controls */}
      <Paper
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          padding: 3,
          borderRadius: 2,
          flex: 1
        }}
      >
        <Typography variant="subtitle2" gutterBottom textAlign="center" sx={{ mb: 1 }}>
          Pan Camera
        </Typography>
        <Typography variant="caption" color="text.secondary" textAlign="center" display="block" sx={{ mb: 2 }}>
          Click to start/stop continuous movement
        </Typography>
        <Stack spacing={2} alignItems="center">
          <Box display="flex" justifyContent="center">
            <ControlButton
              icon={<ArrowUpward fontSize="large" />}
              eventName="remote:camera-move"
              direction="up"
              label="Move Up"
              toggleMode={true}
              controlId="pan-up"
            />
          </Box>
          <Stack direction="row" spacing={2} justifyContent="center" alignItems="center">
            <ControlButton
              icon={<ArrowBack fontSize="large" />}
              eventName="remote:camera-move"
              direction="left"
              label="Move Left"
              toggleMode={true}
              controlId="pan-left"
            />
            <IconButton
              onClick={handleReset}
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                width: 80,
                height: 80,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)'
                }
              }}
              aria-label="Reset Camera"
            >
              <Refresh fontSize="large" />
            </IconButton>
            <ControlButton
              icon={<ArrowForward fontSize="large" />}
              eventName="remote:camera-move"
              direction="right"
              label="Move Right"
              toggleMode={true}
              controlId="pan-right"
            />
          </Stack>
          <Box display="flex" justifyContent="center">
            <ControlButton
              icon={<ArrowDownward fontSize="large" />}
              eventName="remote:camera-move"
              direction="down"
              label="Move Down"
              toggleMode={true}
              controlId="pan-down"
            />
          </Box>
        </Stack>
      </Paper>

      {/* Zoom Controls */}
      <Paper
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          padding: 3,
          borderRadius: 2
        }}
      >
        <Typography variant="subtitle2" gutterBottom textAlign="center" sx={{ mb: 2 }}>
          Zoom
        </Typography>
        <Stack direction="row" spacing={3} justifyContent="center">
          <ControlButton
            icon={<ZoomOut fontSize="large" />}
            eventName="remote:camera-zoom"
            direction="out"
            label="Zoom Out"
            size="medium"
            toggleMode={true}
            controlId="zoom-out"
          />
          <ControlButton
            icon={<ZoomIn fontSize="large" />}
            eventName="remote:camera-zoom"
            direction="in"
            label="Zoom In"
            size="medium"
            toggleMode={true}
            controlId="zoom-in"
          />
        </Stack>
      </Paper>

      {/* Rotation Controls */}
      <Paper
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          padding: 3,
          borderRadius: 2
        }}
      >
        <Typography variant="subtitle2" gutterBottom textAlign="center" sx={{ mb: 2 }}>
          Rotate (Bearing)
        </Typography>
        <Stack direction="row" spacing={3} justifyContent="center">
          <ControlButton
            icon={<RotateLeft fontSize="large" />}
            eventName="remote:camera-rotate"
            direction="left"
            label="Rotate Left"
            size="medium"
            toggleMode={true}
            controlId="rotate-left"
          />
          <ControlButton
            icon={<RotateRight fontSize="large" />}
            eventName="remote:camera-rotate"
            direction="right"
            label="Rotate Right"
            size="medium"
            toggleMode={true}
            controlId="rotate-right"
          />
        </Stack>
      </Paper>

      {/* Tilt Controls */}
      <Paper
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          padding: 3,
          borderRadius: 2
        }}
      >
        <Typography variant="subtitle2" gutterBottom textAlign="center" sx={{ mb: 2 }}>
          Tilt (Pitch)
        </Typography>
        <Stack direction="row" spacing={3} justifyContent="center">
          <ControlButton
            icon={<KeyboardArrowDown fontSize="large" />}
            eventName="remote:camera-tilt"
            direction="down"
            label="Tilt Down"
            size="medium"
            toggleMode={true}
            controlId="tilt-down"
          />
          <ControlButton
            icon={<KeyboardArrowUp fontSize="large" />}
            eventName="remote:camera-tilt"
            direction="up"
            label="Tilt Up"
            size="medium"
            toggleMode={true}
            controlId="tilt-up"
          />
        </Stack>
      </Paper>

      {/* Status Indicator */}
      {isControlling && (
        <Paper
          sx={{
            backgroundColor: 'rgba(33, 150, 243, 0.2)',
            padding: 1.5,
            borderRadius: 2,
            border: '2px solid',
            borderColor: 'primary.main',
            animation: 'glow 1.5s infinite',
            '@keyframes glow': {
              '0%, 100%': { boxShadow: '0 0 10px rgba(33, 150, 243, 0.5)' },
              '50%': { boxShadow: '0 0 20px rgba(33, 150, 243, 0.8)' }
            }
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: 'primary.main',
                animation: 'blink 1s infinite',
                '@keyframes blink': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.3 }
                }
              }}
            />
            <Typography variant="body2" textAlign="center" fontWeight="bold">
              Camera Moving ({activeButtons.size} active)
            </Typography>
          </Stack>
        </Paper>
      )}

      {/* Name Dialog */}
      <Dialog
        open={nameDialogOpen}
        onClose={() => {}}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#1a1a1a',
            color: 'white'
          }
        }}
      >
        <DialogTitle>Save Camera Path</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Path Name"
            fullWidth
            value={pathName}
            onChange={(e) => setPathName(e.target.value)}
            sx={{
              '& .MuiInputBase-root': {
                color: 'white'
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)'
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.5)'
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleSavePath}
            variant="contained"
            sx={{
              backgroundColor: '#22c55e',
              '&:hover': { backgroundColor: '#16a34a' }
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RemoteControl;